import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import MuiDialogActions from '@material-ui/core/DialogActions';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import EditIcon from '@material-ui/icons/Edit';
import Pagination from '@material-ui/lab/Pagination';
import alertInfo from 'assets/alertInfoBlack.svg';
import alertInfoRed from 'assets/alertInfoRed.svg';
import uploadImg from 'assets/upload.png';
import NormalPriorityIcon from 'assets/wg-normal-priority-alert.svg';
import isEmpty from 'lodash/isEmpty';
import WgChip from 'pages/WellGuardian/WgChip';
import { useIntl } from 'react-intl';
import { WIRUser } from 'types/wellGuardian';

import styles from './UserDetailsModalTable.module.scss';
import { getUserRole } from '../../utils';

type HeadCell = {
  id: string;
  label: string | null;
};
const WIR_USERS_PAGE_SIZE = 10;

type UserDetailsModalTableProps = {
  wirUsersForPage: WIRUser[];
  allWirUsers: WIRUser[];
  pageSize: number;
  page: number;
  handlePageChange: (_event: React.ChangeEvent<unknown>, value: number) => void;
  handleUserEditSelect: (item: any) => void;
  handleUserDeleteSelect: (item: any) => void;
  searchText: string;
  multiUser: boolean;
  setMultiUser: (value: boolean) => void;
  excelData: WIRUser[];
  setExcelData: (value: WIRUser[] | null) => void;
  cancelMethod: () => void;
  okAPIMethod: () => void;
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const ALL_WIR = 'ALL';

const headCells: HeadCell[] = [
  { id: 'userId', label: 'User ID' },
  { id: 'userName', label: 'Name' },
  { id: 'emailAddress', label: 'Email' },
  { id: 'country', label: 'Country' },
  { id: 'assets', label: 'Asset' },
  { id: 'roles', label: 'Role' },
  { id: 'edit', label: null },
  { id: 'delete', label: null },
];

const UserDetailsModalTable: React.FunctionComponent<UserDetailsModalTableProps> = ({
  wirUsersForPage,
  allWirUsers,
  pageSize,
  page,
  handlePageChange,
  handleUserEditSelect,
  handleUserDeleteSelect,
  searchText,
  multiUser,
  setMultiUser,
  excelData,
  setExcelData,
  cancelMethod,
  okAPIMethod,
  handleFileUpload,
}) => {
  const intl = useIntl();

  const nonMatchingData = excelData?.filter((item: any) => {
    return !allWirUsers?.some((prefItem: any) => prefItem?.emailAddress === item?.emailAddress);
  });

  const handleMultipleUsersPopup = () => {
    if (multiUser) {
      setMultiUser(false);
    }
    setMultiUser(true);
  };

  const handleCount = () => {
    if (searchText === '') {
      return pageSize;
    } else {
      const data = allWirUsers?.filter(
        x =>
          x?.userId?.toLocaleLowerCase()?.includes(searchText?.toLocaleLowerCase()) ||
          x?.userName?.toLocaleLowerCase()?.includes(searchText?.toLocaleLowerCase()) ||
          x?.emailAddress?.toLocaleLowerCase()?.includes(searchText?.toLocaleLowerCase()) ||
          x?.country?.toLocaleLowerCase()?.includes(searchText?.toLocaleLowerCase()) ||
          x?.assets?.toString()?.toLocaleLowerCase()?.includes(searchText?.toLocaleLowerCase()),
      );

      return Math.ceil(data.length / WIR_USERS_PAGE_SIZE);
    }
  };

  const itemStart = (page - 1) * WIR_USERS_PAGE_SIZE;
  const itemEnd = itemStart + WIR_USERS_PAGE_SIZE;

  return (
    <>
      <TableContainer className={styles.tableContainer} data-testid="user-detail-table-modal">
        <Table>
          <TableHead>
            <TableRow className={styles.tableHeaderRow}>
              {headCells.map(headCell => (
                <TableCell key={headCell.id} align="left" className={styles.tableHeadCell}>
                  {headCell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {excelData === null && searchText === ''
              ? wirUsersForPage.map(item => (
                  <TableRow key={item.userId} className={styles.tableBodyRow}>
                    <TableCell className={styles.tableBodyCell}>
                      <WgChip
                        config={{
                          text: item.userId,
                          className: styles.grayTag,
                        }}
                      />
                    </TableCell>
                    <TableCell className={styles.tableBodyCell}>{item.userName}</TableCell>
                    <TableCell className={styles.tableBodyCell}>{item.emailAddress}</TableCell>
                    <TableCell className={styles.tableBodyCell}>
                      {item.country?.toUpperCase()}
                    </TableCell>
                    <TableCell className={styles.tableBodyCell}>
                      {item?.assets.indexOf(ALL_WIR) === -1 ? item?.assets?.join(', ') : ALL_WIR}
                    </TableCell>
                    <TableCell className={styles.tableBodyCell}>
                      {getUserRole(item.roles)}
                    </TableCell>
                    <TableCell className={styles.tableActionBodyCell}>
                      <EditIcon
                        className={styles.editIcon}
                        onClick={() => handleUserEditSelect(item)}
                        data-testid="edit-icon"
                      />
                    </TableCell>
                    <TableCell className={styles.tableActionBodyCell}>
                      <DeleteOutlineIcon
                        className={styles.deleteIcon}
                        onClick={() => handleUserDeleteSelect(item)}
                        data-testid="delete-icon"
                      />
                    </TableCell>
                  </TableRow>
                ))
              : excelData === null &&
                allWirUsers
                  ?.filter(
                    x =>
                      x?.userId?.toLocaleLowerCase()?.includes(searchText?.toLocaleLowerCase()) ||
                      x?.userName?.toLocaleLowerCase()?.includes(searchText?.toLocaleLowerCase()) ||
                      x?.emailAddress
                        ?.toLocaleLowerCase()
                        ?.includes(searchText?.toLocaleLowerCase()) ||
                      x?.country?.toLocaleLowerCase()?.includes(searchText?.toLocaleLowerCase()) ||
                      x?.assets
                        ?.toString()
                        ?.toLocaleLowerCase()
                        ?.includes(searchText?.toLocaleLowerCase()),
                  )
                  ?.slice(itemStart, itemEnd)
                  ?.map(item => (
                    <TableRow key={item.userId} className={styles.tableBodyRow}>
                      <TableCell className={styles.tableBodyCell}>
                        <WgChip
                          config={{
                            text: item.userId,
                            className: styles.grayTag,
                          }}
                        />
                      </TableCell>
                      <TableCell className={styles.tableBodyCell}>{item.userName}</TableCell>
                      <TableCell className={styles.tableBodyCell}>{item.emailAddress}</TableCell>
                      <TableCell className={styles.tableBodyCell}>
                        {item.country?.toUpperCase()}
                      </TableCell>
                      <TableCell className={styles.tableBodyCell}>
                        {item?.assets.indexOf(ALL_WIR) === -1 ? item?.assets?.join(', ') : ALL_WIR}
                      </TableCell>
                      <TableCell className={styles.tableBodyCell}>
                        {getUserRole(item.roles)}
                      </TableCell>
                      <TableCell className={styles.tableActionBodyCell}>
                        <EditIcon
                          className={styles.editIcon}
                          onClick={() => handleUserEditSelect(item)}
                          data-testid="edit-icon"
                        />
                      </TableCell>
                      <TableCell className={styles.tableActionBodyCell}>
                        <DeleteOutlineIcon
                          className={styles.deleteIcon}
                          onClick={() => handleUserDeleteSelect(item)}
                          data-testid="delete-icon"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
            {excelData !== null &&
              excelData?.map(item => (
                <TableRow key={item?.userId} className={styles.tableBodyRow}>
                  <TableCell className={styles.tableBodyCell}>
                    <WgChip
                      config={{
                        text: item?.userId,
                        className: styles.grayTag,
                      }}
                    />
                  </TableCell>
                  <TableCell className={styles.tableBodyCell}>{item?.userName}</TableCell>
                  <TableCell className={styles.tableBodyCell}>{item?.emailAddress}</TableCell>
                  <TableCell className={styles.tableBodyCell}>
                    {item?.country?.toUpperCase()}
                  </TableCell>
                  <TableCell className={styles.tableBodyCell}>
                    {item?.assets?.indexOf(ALL_WIR) === -1 ? item?.assets?.join(', ') : ALL_WIR}
                  </TableCell>
                  <TableCell className={styles.tableBodyCell}>{getUserRole(item?.roles)}</TableCell>
                  <TableCell className={styles.tableActionBodyCell}>
                    <Tooltip
                      interactive
                      title={
                        item.isDuplicate
                          ? 'This record(s) found as duplicate. Please rectify & re-upload the file.'
                          : nonMatchingData?.find(x => x.emailAddress === item.emailAddress)
                          ? 'The record(s) will be added to the database when you click "Save and Exit".'
                          : 'User already exist. Remove the user in the excels sheet and upload again.'
                      }
                      classes={{ tooltip: styles.tooltips, arrow: styles.arrows }}
                    >
                      <img
                        src={
                          item.isDuplicate
                            ? NormalPriorityIcon
                            : nonMatchingData?.find(x => x.emailAddress === item.emailAddress)
                            ? alertInfo
                            : alertInfoRed
                        }
                        className={item.isDuplicate ? styles.infoAlertDuplicate : styles.infoAlert}
                        alt="alertInfo"
                      />
                    </Tooltip>
                    <EditIcon
                      className={styles.editIconHide}
                      onClick={() => handleUserEditSelect(item)}
                      data-testid="edit-icon"
                    />
                  </TableCell>
                  <TableCell className={styles.tableActionBodyCellDel}>
                    <DeleteOutlineIcon
                      className={styles.deleteIcon}
                      onClick={() => handleUserDeleteSelect(item)}
                      data-testid="delete-icon"
                    />
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!isEmpty(allWirUsers) && !multiUser && (
        <div className={styles.pagination}>
          <Pagination
            classes={{ root: styles.paginationRoot }}
            count={handleCount()}
            page={page}
            onChange={handlePageChange}
          />
        </div>
      )}
      {multiUser && excelData !== null && (
        <div className={styles.multiBtn}>
          <div className={styles.fileUpload}>
            <label htmlFor="file-upload">
              <div
                data-testid="multiPopup"
                className={styles.customFileUpload}
                onClick={handleMultipleUsersPopup}
              >
                <img src={uploadImg} className={styles.uploadImg} alt="" />
                RE-UPLOAD
              </div>
            </label>
            <input
              id="file-upload"
              type="file"
              onChange={handleFileUpload}
              accept=".xlsx"
              style={{ display: 'none' }}
            />
          </div>
          <MuiDialogActions>
            <Button className={styles.cancelButton} onClick={cancelMethod}>
              {intl.formatMessage({
                id: 'wellGuardian.wir.createNewWIRModal.cancel',
              })}
            </Button>
            <Button onClick={okAPIMethod} className={styles.okButton}>
              {intl.formatMessage({
                id: 'wellGuardian.wir.createNewWIRModal.saveAndExit',
              })}
              <ArrowForwardIosIcon fontSize="small" className={styles.buttonIcon} />
            </Button>
          </MuiDialogActions>
        </div>
      )}
    </>
  );
};

export default UserDetailsModalTable;
